import { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth'
import { api } from '../api'
import { useI18n, LangToggle } from '../i18n'
import { useDisplayMode, DisplayModeToggle } from '../displayMode'
import { todayYmd, ymd, addDays, parseYmd } from '../lib/dates'
import { Avatar } from './ui'
import QuickAdd from './QuickAdd'
import {
  IconCalendar,
  IconChart,
  IconClock,
  IconHome,
  IconInbox,
  IconLogout,
  IconMenu,
  IconPlus,
  IconSparkle,
  IconUser,
  IconUsers,
} from './icons'

function greetingKey(): string {
  const h = new Date().getHours()
  return h < 12 ? 'greeting.morning' : h < 18 ? 'greeting.afternoon' : 'greeting.evening'
}

export default function AppLayout() {
  const { user, logout, hasRole } = useAuth()
  const { t, setLang } = useI18n()
  const { setMode } = useDisplayMode()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [pending, setPending] = useState(0)

  // Close the mobile drawer whenever the route changes.
  useEffect(() => setMenuOpen(false), [location.pathname])

  // Apply the signed-in person's preferred language.
  useEffect(() => {
    if (user?.lang) setLang(user.lang)
  }, [user?.lang, setLang])

  // Apply the signed-in person's saved display mode (their choice wins over the
  // device default once they're known).
  useEffect(() => {
    if (user?.display_mode) setMode(user.display_mode)
  }, [user?.display_mode, setMode])

  // Pending-approvals hint for supervisors.
  useEffect(() => {
    if (!hasRole('supervisor')) return
    let active = true
    const from = ymd(addDays(parseYmd(todayYmd()), -14))
    const to = ymd(addDays(parseYmd(todayYmd()), 180))
    api
      .bookings(from, to, 'all')
      .then((r) => {
        if (active) setPending(r.bookings.filter((b) => b.status === 'pending').length)
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [hasRole, location.pathname])

  const firstName = useMemo(() => (user?.name || user?.email || '').split(/[\s@]/)[0], [user])

  if (!user) return null

  const navItem = (to: string, label: string, Icon: typeof IconHome, opts?: { end?: boolean; pip?: number }) => (
    <NavLink key={to} to={to} end={opts?.end} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
      <Icon className="ico" />
      <span>{label}</span>
      {opts?.pip ? <span className="pip">{opts.pip}</span> : null}
    </NavLink>
  )

  return (
    <div className="app-shell">
      {menuOpen && <div className="scrim" onClick={() => setMenuOpen(false)} />}
      <aside className={`sidebar${menuOpen ? ' open' : ''}`}>
        <div className="brand">
          <img className="brand-logo" src="/mark.png" alt="Armon Kappeli" />
          <div>
            <div className="brand-name">Armon Kappeli</div>
            <div className="brand-sub">{t('app.tagline')}</div>
          </div>
        </div>

        <nav className="nav">
          {navItem('/', t('nav.home'), IconHome, { end: true })}
          {navItem('/calendar', t('nav.calendar'), IconCalendar)}
          {navItem('/my-shifts', t('nav.myShifts'), IconClock)}
          {navItem('/profile', t('nav.profile'), IconUser)}

          {hasRole('supervisor') && (
            <>
              <div className="nav-label">{t('nav.manage')}</div>
              {navItem('/approvals', t('nav.approvals'), IconInbox, { pip: pending })}
              {navItem('/open-times', t('nav.openTimes'), IconSparkle)}
              {navItem('/team', t('nav.team'), IconUsers)}
              {navItem('/balance', t('nav.balance'), IconChart)}
            </>
          )}
        </nav>

        <div className="side-foot">
          <div className="user-chip">
            <Link
              to="/profile"
              className="grow row gap-8"
              style={{ minWidth: 0, alignItems: 'center', color: 'inherit' }}
              title={t('nav.profile')}
            >
              <Avatar name={user.name} email={user.email} green />
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.name || t('user.addName')}
                </div>
                <div className="role">{t('role.' + user.role)}</div>
              </div>
            </Link>
            <button className="iconbtn" title={t('common.signOut')} onClick={() => logout()}>
              <IconLogout width={18} height={18} />
            </button>
          </div>
          <div className="side-lang"><DisplayModeToggle /></div>
          <div className="side-lang"><LangToggle /></div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="row">
            <button className="iconbtn menu-btn" onClick={() => setMenuOpen(true)} aria-label={t('nav.openMenu')}>
              <IconMenu />
            </button>
            <div>
              <div className="eyebrow">{t(greetingKey())}</div>
              <h1>{firstName ? t('topbar.hi', { name: firstName }) : t('topbar.welcome')}</h1>
            </div>
          </div>
          <div className="row">
            <button className="btn btn-primary" onClick={() => navigate('/calendar')}>
              <IconPlus width={18} height={18} /> {t('topbar.bookShift')}
            </button>
          </div>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>

      <QuickAdd />
    </div>
  )
}
